import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  CHANNEL_ROLES,
  CONTEST_ROLES,
  EXAM_ROLES,
  PLATFORM_FIN_ROLES,
  SELLER_ROLES,
  STAFF_ROLES,
  adminApi,
} from '../api/admin.js';
import { bankApi } from '../api/bank.js';
import { createClient } from '../api/client.js';
import { commerceApi } from '../api/commerce.js';
import { teachingApi } from '../api/teaching.js';
import { contestAdminApi, reviewApi } from '../api/contest.js';
import { siteApi } from '../api/site.js';
import { examApi } from '../api/exam.js';
import { learningApi } from '../api/learning.js';
import { mediaApi } from '../api/media.js';
import { notifyApi } from '../api/notify.js';
import { payApi } from '../api/pay.js';
import { settingsApi } from '../api/settings.js';
import { clearRef, readRef } from '../ui/referral.js';
import { siteCodeFromHost } from '../ui/site-host.js';
import { useViewport } from './viewport.js';

interface TokenPair {
  accessToken: string;
  accessExpiresAt: number;
  refreshToken: string;
  refreshExpiresAt: number;
}

export interface Account {
  id: string;
  name: string;
  /** 本人的登录手机(页面上显示时脱敏) */
  phone?: string;
  roles: { role: string; tenantId: string }[] | string[];
  hasPassword?: boolean;
}
interface LoginResult {
  account: Account;
  tokens: TokenPair;
  mustChangePassword?: boolean;
}
/** 密码对了但要短信二次验证(平台管理员 / 财务每次;其他后台角色新设备 / 新地点) */
export interface MfaChallenge {
  mfaRequired: true;
  challengeId: string;
  phone: string;
  reason: 'always' | 'new_device' | 'new_place';
  resendAfterSec: number;
  devCode?: string;
  lastLogin: { at: string; ip: string | null; device: string } | null;
  current: { ip: string | null; device: string };
}
export interface Me extends Account {
  siteRoles: string[];
  studentId: string | null;
  mustChangePassword?: boolean;
}
export interface StudentProfile {
  id: string;
  accountId: string;
  name: string;
  nickname: string;
  /** 平台头像库的键(a1–a9) */
  avatar?: string;
  birthMonth: string;
  ageBand: string;
  schoolStage: string;
  grade: string | null;
  /** 自填的学校名 */
  school: string | null;
  /** 在公立学校在读(学校导入编班):学生端不出价格与购买入口;家长端按学校的付费提醒 */
  publicSchool?: { tenantId: string; name: string; outside: 'hidden' | 'labeled' } | null;
  attributionTenantId: string | null;
  status: string;
  lessonHours: number;
  /** 授权状态:pending_verify = 机构导入、还没完成监护人验证(学习与考试入口先去首次授权引导) */
  consentState?: string;
}

export interface RegisterInput {
  phone: string;
  smsCode: string;
  name: string;
  nickname: string;
  birthMonth: string;
  schoolStage: string;
  grade?: string;
  faceCapture: boolean;
  agreed: boolean;
  /** 推广链接码(从 /r/:code 落地页记下的) */
  ref?: string;
}

const STORAGE_KEY = 'cg.session';
const TENANT_KEY = 'cg.tenant';

function readTenantOverride(): string {
  try {
    return localStorage.getItem(TENANT_KEY) ?? '';
  } catch {
    return '';
  }
}

/** 会话:令牌 + 当前账号 + 当前学员;刷新令牌轮换;租户码来自子域名或本地开发配置。 */
export const useSession = defineStore('session', () => {
  const tokens = ref<TokenPair | null>(load());
  /** 后台账号还在用初始密码:外壳弹不可关的「请先修改密码」 */
  const mustChangePassword = ref(false);
  const me = ref<Me | null>(null);
  const student = ref<StudentProfile | null>(null);
  const students = ref<StudentProfile[]>([]);

  /**
   * 站点码:「<站点码>.<平台主域名>」子域名 > 本机覆盖(开发 / 演示时切换机构站点)> 构建配置 >
   * 服务端按自有域名解析出的(A18-5:机构自己的域名不从主机名猜,第一次请求不带站点码头,服务端按域名认出机构,
   * /v1/site/tenant 把站点码带回来,之后的请求照常带)
   */
  const tenantOverride = ref<string>(readTenantOverride());
  const resolvedSite = ref<string>('');
  const hostSite = computed(() =>
    typeof location !== 'undefined'
      ? siteCodeFromHost(location.hostname, import.meta.env['VITE_PLATFORM_DOMAIN'] as string | undefined)
      : null,
  );
  const tenantCode = computed(() => {
    if (hostSite.value) return hostSite.value;
    if (tenantOverride.value) return tenantOverride.value;
    const built = import.meta.env['VITE_TENANT_CODE'] as string | undefined;
    if (built) return built;
    return resolvedSite.value || undefined;
  });
  /** 站点是按地址认出来的(子域名或机构自有域名):登录页不出开发用的「切换站点」 */
  const siteFromAddress = computed(() => !!hostSite.value || (!tenantOverride.value && !!resolvedSite.value));
  /** /v1/site/tenant 回来的站点码(服务端按请求地址解析;平台站点为空) */
  function setResolvedSite(code: string | null | undefined) {
    resolvedSite.value = code && code !== 'platform' ? code : '';
  }
  function setTenantOverride(code: string) {
    tenantOverride.value = code.trim().toLowerCase();
    try {
      if (tenantOverride.value) localStorage.setItem(TENANT_KEY, tenantOverride.value);
      else localStorage.removeItem(TENANT_KEY);
    } catch {
      /* ignore */
    }
  }

  /** 端标识(x-client):手机上是 h5(学生端 H5),其余是 web;服务端按它记埋点的 client 维度 */
  const viewport = useViewport();
  const client = () => viewport.client;

  /** 用刷新令牌换一对新令牌(过期自动续;角色变了(如刚绑定孩子成为家长)也调它让新角色生效) */
  async function refreshTokens(): Promise<string | undefined> {
    if (!tokens.value) return undefined;
    try {
      const next = await createClient({ tenantCode: () => tenantCode.value, client }).post<TokenPair>(
        '/v1/auth/refresh',
        {
          refreshToken: tokens.value.refreshToken,
        },
      );
      setTokens(next);
      return next.accessToken;
    } catch {
      clear();
      return undefined;
    }
  }
  const api = createClient({
    tenantCode: () => tenantCode.value,
    accessToken: () => tokens.value?.accessToken,
    refresh: refreshTokens,
    client,
  });
  const learning = learningApi(api);
  const exam = examApi(api);
  const media = mediaApi(api);
  const admin = adminApi(api);
  const contest = contestAdminApi(api);
  const review = reviewApi(api);
  const site = siteApi(api);
  const notify = notifyApi(api);
  const settings = settingsApi(api);
  const commerce = commerceApi(api);
  const teaching = teachingApi(api);
  const bank = bankApi(api);
  const pay = payApi(api);

  function setTokens(t: TokenPair | null) {
    tokens.value = t;
    try {
      if (t) localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* 隐私模式等 */
    }
  }
  function clear() {
    setTokens(null);
    me.value = null;
    student.value = null;
    students.value = [];
    mustChangePassword.value = false;
  }

  /** 登录成功的共同收尾:存令牌、取当前账号;初始密码的后台账号标记「必须先改密码」 */
  async function applyLogin(r: LoginResult) {
    setTokens(r.tokens);
    mustChangePassword.value = !!r.mustChangePassword;
    await loadMe();
    return r.account;
  }
  async function loginBySms(phone: string, code: string, remember = false) {
    return applyLogin(await api.post<LoginResult>('/v1/auth/login/sms', { phone, code, remember }));
  }
  /** 密码登录:后台账号要二次验证时返回挑战(页面转到输入验证码),否则直接登录 */
  async function loginByPassword(
    phone: string,
    password: string,
    remember = false,
  ): Promise<{ mfa: MfaChallenge } | { account: Account }> {
    const r = await api.post<LoginResult | MfaChallenge>('/v1/auth/login/password', {
      phone,
      password,
      remember,
    });
    if ('mfaRequired' in r) return { mfa: r };
    return { account: await applyLogin(r) };
  }
  async function verifyMfa(challengeId: string, code: string) {
    return applyLogin(await api.post<LoginResult>('/v1/auth/login/mfa', { challengeId, code }));
  }
  function resendMfa(challengeId: string) {
    return api.post<{ resendAfterSec: number; devCode?: string }>('/v1/auth/login/mfa/resend', {
      challengeId,
    });
  }
  function denyLogin(challengeId: string) {
    return api.post<{ ok: boolean }>('/v1/auth/login/mfa/deny', { challengeId });
  }
  /** 登录异常提醒里点「不是我本人」(已登录的本人):这个账号所有设备下线 + 留痕,接着去找回密码 */
  function notMe() {
    return api.post<{ ok: boolean }>('/v1/auth/not-me', {});
  }
  function requestResetCode(phone: string) {
    return api.post<{ resendAfterSec: number; devCode?: string }>('/v1/auth/sms-code', {
      phone,
      purpose: 'reset',
    });
  }
  /** 找回密码:成功后本站点有角色就直接登录(返回 true),否则只重置(返回 false) */
  async function resetPassword(phone: string, code: string, newPassword: string, remember = false) {
    const r = await api.post<{ reset: boolean; login: LoginResult | null }>('/v1/auth/password/reset', {
      phone,
      code,
      newPassword,
      remember,
    });
    if (!r.login) return false;
    await applyLogin(r.login);
    return true;
  }
  /** 改密码(旧密码或本人验证码):其他设备下线,本设备换新令牌 */
  async function changePassword(input: { oldPassword?: string; code?: string; newPassword: string }) {
    const r = await api.post<{ tokens: TokenPair }>('/v1/auth/password/change', input);
    setTokens(r.tokens);
    mustChangePassword.value = false;
  }
  function requestOwnPasswordCode() {
    return api.post<{ resendAfterSec: number; devCode?: string }>('/v1/auth/password/code', {});
  }
  /** 大赛官网 → 考试平台的一次性交接码(60 秒、只能用一次):换本站点令牌(带学员),不用再登录 */
  async function loginByHandoff(code: string) {
    const r = await api.post<{ tokens: TokenPair; to: string }>('/v1/auth/handoff', { code });
    setTokens(r.tokens);
    await loadMe();
    return r.to;
  }
  /** 学生自注册(设计图 05):成功即登录并带上学员;经推广链接进来的带上链接码(首次绑定归属) */
  /** 注册;accountExisted = 手机号原来就有账号(没新建账号,是在这个账号下新增了孩子,A1-4) */
  async function registerStudent(input: RegisterInput) {
    const ref = input.ref ?? readRef();
    const r = await api.post<{ tokens: TokenPair; student: { id: string }; accountExisted?: boolean }>(
      '/v1/students/register',
      {
        ...input,
        ...(ref ? { ref } : {}),
      },
    );
    clearRef();
    setTokens(r.tokens);
    await loadMe();
    return { studentId: r.student.id, accountExisted: !!r.accountExisted };
  }
  async function requestSmsCode(phone: string, purpose: 'login' | 'register' = 'login') {
    return api.post<{ resendAfterSec: number; devCode?: string }>('/v1/auth/sms-code', { phone, purpose });
  }
  async function loadMe() {
    me.value = await api.get<Me>('/v1/auth/me');
    mustChangePassword.value = !!me.value.mustChangePassword;
    if (me.value.studentId)
      student.value = await api.get<StudentProfile>('/v1/students/current').catch(() => null);
    else student.value = null;
    return me.value;
  }
  async function loadStudents() {
    students.value = await api.get<StudentProfile[]>('/v1/students/mine');
    return students.value;
  }
  /** 切换当前学员:换发带学员的令牌 */
  async function selectStudent(id: string) {
    const r = await api.post<{ tokens: TokenPair }>(`/v1/students/${id}/select`);
    setTokens(r.tokens);
    await loadMe();
  }
  async function logout() {
    const rt = tokens.value?.refreshToken;
    clear();
    if (rt) await api.post('/v1/auth/logout', { refreshToken: rt }).catch(() => undefined);
  }

  const isStudent = computed(() => me.value?.siteRoles.includes('student') ?? false);
  const isParent = computed(() => me.value?.siteRoles.includes('parent') ?? false);
  /** 本站点的员工角色(机构管理员 / 教师 / 考试管理员 / 监考员 / 平台);有就能进机构后台 */
  const staffRoles = computed(() => (me.value?.siteRoles ?? []).filter((r) => STAFF_ROLES.includes(r)));
  const isStaff = computed(() => staffRoles.value.length > 0);
  const isOrgAdmin = computed(() =>
    ['org_admin', 'platform_admin'].some((r) => me.value?.siteRoles.includes(r)),
  );
  const hasAny = (roles: string[]) => roles.some((r) => me.value?.siteRoles.includes(r));
  /** 主办方后台(赛事)与评审员工作台 */
  const canContest = computed(() => hasAny(CONTEST_ROLES));
  /** 考务 / 合作机构后台 / 课程包与订单 / 平台渠道与结算(平台站点才有) */
  const canExam = computed(() => hasAny(EXAM_ROLES));
  /** 学员与班级 / 批改与预警 */
  const canTeach = computed(() => hasAny(['org_admin', 'teacher', 'platform_admin', 'platform_ops']));
  /** 题库 / 试卷 / 知识图谱(考试管理员只读题、能组卷;出题人、平台内容能出题) */
  const canBank = computed(() =>
    hasAny(['org_admin', 'teacher', 'question_author', 'exam_admin', 'platform_admin', 'platform_content']),
  );
  /** 能看隔离池(正式考 / 赛题)题目内容 */
  const canIsolated = computed(() =>
    hasAny(['org_admin', 'question_author', 'exam_admin', 'organizer', 'platform_admin', 'platform_content']),
  );
  const isChannel = computed(() => hasAny(CHANNEL_ROLES));
  const isChannelL1 = computed(() => hasAny(['channel_l1']));
  const canSell = computed(() => hasAny(SELLER_ROLES));
  const isPlatformSite = computed(() => !tenantCode.value || tenantCode.value === 'platform');
  const canPlatformFinance = computed(() => isPlatformSite.value && hasAny(PLATFORM_FIN_ROLES));
  /** 平台运营后台(机构与租户等):平台管理员 / 平台运营,在平台站点上 */
  const canPlatformOps = computed(() => isPlatformSite.value && hasAny(['platform_admin', 'platform_ops']));
  /** 审计日志:平台看全部(平台管理员 / 审计只读);机构只看本租户(机构 / 主办方管理员、合作机构、公立学校) */
  const canPlatformAudit = computed(
    () => isPlatformSite.value && hasAny(['platform_admin', 'platform_audit']),
  );
  const canTenantAudit = computed(
    () =>
      !isPlatformSite.value &&
      hasAny(['org_admin', 'organizer', 'channel_l1', 'channel_l2', 'public_school']),
  );
  const isPlatformAdmin = computed(() => hasAny(['platform_admin']));
  /** 规则配置中心:平台员工都能看(能改哪一类后端按角色给) */
  const canPlatformRules = computed(
    () =>
      isPlatformSite.value &&
      hasAny([
        'platform_admin',
        'platform_ops',
        'platform_finance',
        'platform_content',
        'platform_support',
        'platform_audit',
      ]),
  );
  /** 数据统计(平台站点):平台管理员 / 运营 / 财务 / 内容 / 审计只读 */
  const canPlatformStats = computed(
    () =>
      isPlatformSite.value &&
      hasAny(['platform_admin', 'platform_ops', 'platform_finance', 'platform_content', 'platform_audit']),
  );
  /** 数字人中心(平台站点):形象库 / 话术库 / 关卡素材 / 审核队列 —— 平台管理员、运营、内容 */
  const canPlatformDh = computed(
    () => isPlatformSite.value && hasAny(['platform_admin', 'platform_ops', 'platform_content']),
  );
  /** 数据看板(机构站点,同一口径的本租户切片):机构管理员 / 主办方 */
  const canOrgStats = computed(() => !isPlatformSite.value && hasAny(['org_admin', 'organizer']));
  /** 品牌与模块(机构站点):机构 / 主办方管理员、合作机构 —— 看模块、在线自助开通、品牌与白标 */
  const canModules = computed(
    () => !isPlatformSite.value && hasAny(['org_admin', 'organizer', 'channel_l1', 'channel_l2']),
  );
  /** 通知与公告(机构站点):机构管理员、教师(只发自己的班)、考试管理员(发考生)、主办方(发报名者) */
  const canNotices = computed(
    () =>
      !isPlatformSite.value && hasAny(['org_admin', 'teacher', 'exam_admin', 'organizer', 'platform_admin']),
  );
  /** 补考页(建补考 / 故障重考、审批学生补考申请):机构管理员、考试管理员、平台管理员 / 运营 */
  const canMakeup = computed(() => hasAny(['org_admin', 'exam_admin', 'platform_admin', 'platform_ops']));
  /** 考务管理动作(第三视角改参数 / 重置二维码 / 批量出码等):同上;监考员只看与提醒 */
  const canExamManage = canMakeup;
  /** 第三视角监考页:考务管理 + 监考员 */
  const canThirdView = computed(() =>
    hasAny(['org_admin', 'exam_admin', 'proctor', 'platform_admin', 'platform_ops']),
  );
  const isOrganizerOnly = computed(
    () =>
      !!me.value?.siteRoles.includes('organizer') &&
      !['org_admin', 'exam_admin', 'teacher', 'proctor'].some((r) => me.value?.siteRoles.includes(r)),
  );
  const isReviewer = computed(() => me.value?.siteRoles.includes('reviewer') ?? false);
  /** 线下赛裁判(移动端打分;主办方加裁判时按手机号建号或加角色) */
  const isJudge = computed(() => me.value?.siteRoles.includes('judge') ?? false);
  /** 当前学员在公立学校在读:学生端任何页面不出价格与购买入口(B13-1 · A3-5) */
  const inPublicSchool = computed(() => !!student.value?.publicSchool);
  const hideRank = computed(() => isPrimaryLow(student.value));

  return {
    api,
    refreshTokens,
    learning,
    exam,
    media,
    admin,
    contest,
    review,
    site,
    notify,
    settings,
    commerce,
    teaching,
    bank,
    pay,
    tokens,
    me,
    student,
    students,
    tenantCode,
    tenantOverride,
    setTenantOverride,
    siteFromAddress,
    setResolvedSite,
    accessToken: computed(() => tokens.value?.accessToken),
    inPublicSchool,
    isStudent,
    isParent,
    staffRoles,
    isStaff,
    isOrgAdmin,
    canContest,
    canExam,
    canTeach,
    canBank,
    canIsolated,
    isChannel,
    isChannelL1,
    canSell,
    isPlatformSite,
    canPlatformFinance,
    canPlatformOps,
    canPlatformAudit,
    canTenantAudit,
    isPlatformAdmin,
    canPlatformRules,
    canPlatformStats,
    canPlatformDh,
    canOrgStats,
    canModules,
    canNotices,
    canMakeup,
    canExamManage,
    canThirdView,
    isOrganizerOnly,
    isReviewer,
    isJudge,
    hideRank,
    mustChangePassword,
    loginBySms,
    loginByPassword,
    verifyMfa,
    resendMfa,
    denyLogin,
    notMe,
    requestResetCode,
    resetPassword,
    changePassword,
    requestOwnPasswordCode,
    loginByHandoff,
    registerStudent,
    requestSmsCode,
    loadMe,
    loadStudents,
    selectStudent,
    logout,
  };
});

/** 小学低年级(1–3 年级):学生端与家长端都不出名次,只给等级与超越百分比(基线 5 · A2-2) */
export function isPrimaryLow(s: { schoolStage: string; grade: string | null } | null | undefined): boolean {
  if (!s || s.schoolStage !== 'primary') return false;
  const n = /^([一二三四五六]|\d)/.exec(s.grade ?? '')?.[1] ?? '';
  return ['一', '二', '三', '1', '2', '3'].includes(n);
}

function load(): TokenPair | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as TokenPair) : null;
  } catch {
    return null;
  }
}
